import React from 'react';
import { Checkbox } from './checkbox';
import { cn } from '../../lib/utils';



export interface GraphParameter {
  value: string;
  label: string;
  color: string;
}

interface GraphParameterSelectorProps {
  parameters: GraphParameter[];
  selected: string[];
  onChange: (selected: string[]) => void;
  className?: string;
  title?: string;
  availableData?: Record<string, boolean>; // Track which parameters have data
}

export function GraphParameterSelector({
  parameters,
  selected,
  onChange,
  className,
  title = "Select Parameters to Display",
  availableData = {}
}: GraphParameterSelectorProps) {
  const handleParameterToggle = (parameterValue: string) => {
    const isSelected = selected.includes(parameterValue);
    
    if (isSelected) {
      // Remove parameter
      const newSelected = selected.filter(p => p !== parameterValue);
      onChange(newSelected);
    } else {
      // Add parameter
      const newSelected = [...selected, parameterValue];
      onChange(newSelected);
    }
  };

  const handleSelectAll = () => {
    // Get only parameters that have data
    const availableParameters = parameters.filter(p => availableData[p.value] !== false);
    
    if (selected.length === availableParameters.length) {
      // Deselect all available parameters
      onChange([]);
    } else {
      // Select all available parameters
      onChange(availableParameters.map(p => p.value));
    }
  };

  // Get only parameters that have data
  const availableParameters = parameters.filter(p => availableData[p.value] !== false);
  const allSelected = selected.length === availableParameters.length && availableParameters.length > 0;
  const someSelected = selected.length > 0 && selected.length < availableParameters.length;

  return (
    <div className={cn("bg-white rounded-lg border border-gray-200 p-4 transition-all duration-300 ease-in-out", className)}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-700">{title}</h3>
        <button
          onClick={handleSelectAll}
          disabled={availableParameters.length === 0}
          className={cn(
            "text-xs font-medium px-3 py-1.5 rounded-md transition-all duration-300 ease-in-out border",
            availableParameters.length === 0
              ? "text-gray-400 border-gray-200 cursor-not-allowed"
              : allSelected 
                ? "text-red-700 cursor-pointer" 
                : "text-red-600 cursor-pointer"
          )}
        >
          {availableParameters.length === 0 
            ? 'No Data Available' 
            : allSelected 
              ? 'Deselect All' 
              : `Select All (${availableParameters.length})`
          }
        </button>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-8 gap-3">
        {availableParameters.map((parameter) => {
          const isSelected = selected.includes(parameter.value);
          return (
            <div
              key={parameter.value}
              className={cn(
                "flex items-center gap-2 p-2 rounded-md select-none transition-all duration-300 ease-in-out transform border cursor-pointer",
                isSelected
                  ? "border-gray-300 hover:scale-[1.01]"
                  : "border-gray-200 hover:border-gray-300 hover:scale-[1.01]"
              )}
              onClick={() => handleParameterToggle(parameter.value)}
            >
              <Checkbox
                checked={isSelected}
                className="pointer-events-none data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600 border-gray-300 hover:border-gray-400 transition-all duration-200 ease-in-out"
              />
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0 transition-all duration-300 ease-in-out"
                  style={{ 
                    backgroundColor: parameter.color,
                    transform: isSelected ? 'scale(1.2)' : 'scale(1)',
                    boxShadow: isSelected ? '0 0 8px rgba(220, 38, 38, 0.3)' : 'none'
                  }}
                />
                <span className={cn(
                  "text-xs truncate transition-all duration-300 ease-in-out",
                  isSelected ? "text-red-700 font-semibold" : "text-gray-700"
                )}>
                  {parameter.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="mt-3 text-xs text-gray-500 text-center transition-all duration-300 ease-in-out">
        <span className="font-medium text-red-600">{selected.length}</span> of {availableParameters.length} available parameters selected
      </div>
    </div>
  );
}
